import {
  BASE_CONTRACT_INELIGIBILITY_REASONS,
  type BaseContractIneligibility,
  type BaseContractNomination,
  type ListBaseContractsResponse,
} from '@clm/shared-types';

import { ApiError } from '../api/errors';
import { classifyApiError, type ApiProblemKind } from '../api/problem';

/**
 * The pure half of the Admin **Base MSA** surface and the contract record's
 * base badge — CLM-111. Kept apart from `BaseMsa.tsx` for the
 * `field-accuracy-rows.ts` reason: a component module that also exports
 * functions breaks fast refresh, and the copy is shared by two surfaces.
 *
 * Role lists are *display hints* (`mayAttempt`, `permissions.ts`): the API's
 * action check is the gate. An identity that names no role sees every control
 * enabled and the API's 403 (D-82-1).
 */

/** The roles the gateway grants `clause:review_match` to — who may read the base (D-2.1-11). */
export const BASE_READER_ROLES: readonly string[] = ['admin', 'legal_reviewer'];
/** The roles the gateway grants `admin:configure` to — who may nominate (FR-37). */
export const BASE_NOMINATOR_ROLES: readonly string[] = ['admin'];

/** The 403 copy for the list: `clause:review_match`, Admin and Legal (R-111-6). */
export const BASE_MSA_FORBIDDEN_COPY =
  'The base MSA is read by administrators and CEQ-Legal (clause:review_match). Your role may read the contracts in ' +
  'your scope, but not which one is the company base.';

/** Shown to a reader who is not an admin, in place of the nominate form. */
export const BASE_MSA_READ_ONLY_COPY =
  'Nominating or replacing the base MSA is an administrator action (admin:configure). You can read the current base ' +
  'and every earlier version.';

/** Why a previewed contract cannot be the base — the page's words for the API's codes (R-111-5). */
export const INELIGIBLE_COPY: Readonly<Record<BaseContractIneligibility, string>> = Object.freeze({
  wrong_contract_type: 'This contract is not an MSA, so it cannot be the base MSA.',
  /* CLM-110 (R-110-21) — a base must have a confirmed type: a wrong one corrupts every later deviation report. */
  type_provisional:
    'This contract’s type is not settled — it is provisional or Type pending, and no administrator has confirmed it ' +
    'yet. Confirm the type first, on the contract record or the Type proposals page, then nominate it.',
  amendment:
    'This contract amends another agreement and holds only the changes to it. Nominate the agreement it amends instead.',
  not_signed:
    'This contract is a Draft. The base MSA is a signed agreement — never an unsigned one, and never a blank template.',
  superseded: 'This agreement has been superseded. Nominate the agreement that replaced it instead.',
  processing_restricted:
    'This contract is processing-restricted (§4C): no model has matched its clauses, so a deviation report against it ' +
    'would silently compare less than it says.',
  /* CLM-118 (R-118-13, B26), CLM-121 (R-121-14) — settled with no person, either kind: the base needs an administrator's confirmation. */
  type_not_admin_confirmed:
    'This contract’s type was settled with no administrator step — because the file name and the AI agree, or by ' +
    'auto-approval for AI-decided types — and no administrator has checked it. A base MSA’s type is confirmed by an ' +
    'administrator: Confirm type and set as base records your confirmation of the type first, then the nomination.',
});

/**
 * What a practices-scoped admin who sees no base is told in place of "this
 * becomes version 1" (R-111-7). Under a practices scope `current: null` means
 * *no base is visible to you*, not *there is none*: the list is scoped on the
 * nominated contract, so a base whose contract is outside the caller's scope
 * is not listed, and nominating then meets `ux_base_contract_nomination_current`
 * as `409 base_changed`. A first nomination may still be exactly right — when
 * no base exists anywhere — so the action stays, and the page says only what
 * it knows.
 */
export function scopedWithoutBaseCopy(type: string): string {
  return (
    `No base ${type} is visible in your scope. If there is none, this contract becomes the base ${type}, version 1; ` +
    `if one exists outside your scope, the API refuses and nothing is recorded — only an admin who can see that ` +
    `base can replace it.`
  );
}

/**
 * A refusal the page explains in its own words. `up` — the neutral tone — for
 * a 409: nothing failed, the base or the contract moved on. `bad` for a 400:
 * this contract cannot be the base, in the preview's own tone.
 */
export interface NominationRefusalCopy {
  readonly code: string;
  readonly tone: 'up' | 'bad';
  readonly head: string;
  readonly body: string;
}

/** A failed nomination as both surfaces hold it. */
export interface NominationProblem {
  readonly kind: ApiProblemKind;
  /**
   * `null` for anything this build has no words for — a 403, a 404, a 5xx, a
   * code it does not know — which `ErrorNotice` renders by kind. In particular
   * a 404 keeps its one sentence: absent and out of scope are the same answer.
   */
  readonly refusal: NominationRefusalCopy | null;
  readonly correlation_id: string | null;
}

/** The 409 codes, each with what happened and what the page did about it. */
const CONFLICT_COPY: Readonly<Record<string, { readonly head: string; readonly body: string }>> = Object.freeze({
  base_changed: {
    head: 'The base MSA changed since this page loaded',
    body: 'Another nomination reached it first, so nothing was recorded. The current nomination has been reloaded — check it, then decide again.',
  },
  already_current: {
    head: 'That contract is already the current base MSA',
    body: 'Nothing was recorded; the base is unchanged.',
  },
  contract_changed: {
    head: 'The contract changed while it was being nominated',
    body: 'It is no longer eligible, so nothing was recorded. Load it again to see what changed.',
  },
});

/**
 * `base_changed` for a practices caller who saw no base. The reload cannot
 * settle it — it answers `current: null` again when the base is outside the
 * caller's scope — so "it changed, decide again" would send them round a loop
 * with a false explanation each time. Both causes are named, because the page
 * cannot tell them apart and the API deliberately does not say (no oracle).
 */
const SCOPED_BASE_CHANGED = Object.freeze({
  head: 'A base MSA is current, and your scope may not show it',
  body:
    'No base was visible in your scope, and the API refused a first nomination because one exists. Either it was ' +
    'nominated since this page loaded — the reload shows it if its contract is in your scope — or its contract is ' +
    'outside your scope, and only an admin who can see it can replace it. Nothing was recorded.',
});

function isIneligibility(code: string): code is BaseContractIneligibility {
  return (BASE_CONTRACT_INELIGIBILITY_REASONS as readonly string[]).includes(code);
}

function refusalCopy(
  kind: ApiProblemKind,
  code: string | null,
  context: { readonly scopedWithoutBase: boolean },
): NominationRefusalCopy | null {
  if (code === null) return null;
  /* A code is trusted only with the status the API sends it under, so a stray field error cannot borrow its words. */
  if (kind === 'conflict') {
    if (code === 'base_changed' && context.scopedWithoutBase) return { code, tone: 'up', ...SCOPED_BASE_CHANGED };
    const copy = CONFLICT_COPY[code];
    return copy === undefined ? null : { code, tone: 'up', ...copy };
  }
  if (kind === 'validation' && isIneligibility(code)) {
    return { code, tone: 'bad', head: 'Not nominated — this contract cannot be the base MSA', body: INELIGIBLE_COPY[code] };
  }
  return null;
}

/**
 * A failed nominate call as the page shows it. `scopedWithoutBase` is the
 * state the call was made from — a practices scope and no visible base —
 * because `base_changed` means something different there.
 */
export function nominationProblemOf(
  error: unknown,
  context: { readonly scopedWithoutBase: boolean },
): NominationProblem {
  const kind = classifyApiError(error);
  return {
    kind,
    refusal: refusalCopy(kind, refusalCodeOf(error), context),
    correlation_id: error instanceof ApiError ? error.correlation_id : null,
  };
}

/**
 * The code of the first `field_errors` entry of an API error — the
 * `code: sentence` convention (`base_changed: …`) — or `null` when there is
 * none. Read, never trusted: an absent or malformed body is `null`.
 */
export function refusalCodeOf(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const body = error.body as { field_errors?: unknown } | null | undefined;
  if (!Array.isArray(body?.field_errors)) return null;
  const first = (body.field_errors as { message?: unknown }[])[0];
  if (typeof first?.message !== 'string') return null;
  const code = first.message.split(':', 1)[0]?.trim() ?? '';
  return code === '' ? null : code;
}

/**
 * The current nomination whose contract is `contractKey`, across every
 * enabled type, or `null`. Keyed on the contract, not on its type, so a base
 * whose type was corrected after nomination (R-111-9) still shows its badge.
 */
export function currentBaseOf(
  response: ListBaseContractsResponse,
  contractKey: string,
): { readonly contract_type: string; readonly nomination: BaseContractNomination } | null {
  for (const entry of response.types) {
    if (entry.current !== null && entry.current.contract_key === contractKey) {
      return { contract_type: entry.contract_type, nomination: entry.current };
    }
  }
  return null;
}
